import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MessageCircle, FileText, Hash, Globe, Lock, ArrowRight, ChevronLeft, Image as ImageIcon, Camera, X, Shield, Key, Loader2 } from 'lucide-react';
import { cn, getDiscussionPath } from '../utils';
import { useActivities } from '../context/ActivitiesContext';
import { useToast } from '../context/ToastContext';
import Breadcrumbs from '../components/Breadcrumbs';
import { handleImageUpload } from '../utils/imageUtils';

const DiscussionCreation = () => {
  const { id: community_id } = useParams();
  const navigate = useNavigate();
  const { addDiscussion } = useActivities();
  const { addToast } = useToast();
  const [step, setStep] = useState(1);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [discussionImage, setDiscussionImage] = useState(null);
  const [privacy, setPrivacy] = useState('public');
  const [password, setPassword] = useState('');
  const [inviteOnly, setInviteOnly] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = React.useRef(null);

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        const resizedImage = await handleImageUpload(file, { maxSizeMB: 2 });
        setDiscussionImage(resizedImage);
      } catch (err) {
        addToast(err.message, 'error');
      }
    }
  };

  const TITLE_LIMIT = 100;
  const CONTENT_LIMIT = 5000;

  const handleCreate = async () => {
    if (!title.trim() || !content.trim()) {
      addToast('Please provide both a title and content for your discussion.', 'error');
      return;
    }

    if (privacy === 'private' && !password.trim()) {
      addToast('Please set a password for your private discussion.', 'error');
      return;
    }

    setIsSubmitting(true);
    
    try {
      const newDiscussion = await addDiscussion({
        title: title.trim(),
        content: content.trim(),
        image: discussionImage,
        community_id,
        privacy,
        settings: {
          password: privacy === 'private' ? password : null,
          invite_only: privacy === 'private' ? inviteOnly : false
        },
        author: 'You',
        replies: '0'
      });
      addToast('Discussion created successfully!', 'success');
      navigate(getDiscussionPath(newDiscussion));
    } catch (err) {
      console.error('Failed to create discussion', err);
      addToast(err.message || 'Failed to create discussion. Please try again.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8 md:py-12">
      <div className="flex items-center gap-4 mb-10">
        <button 
          onClick={() => step > 1 ? setStep(step - 1) : navigate(-1)}
          className="p-3 hover:bg-gray-100 rounded-2xl transition-all text-gray-400 hover:text-gray-900"
        >
          <ChevronLeft size={24} />
        </button>
        <div>
          <div className="mb-1">
            <Breadcrumbs 
              items={[
                { label: 'Communities', path: '/communities' },
                { label: 'Community', path: `/c/${community_id}` },
                { label: 'New Discussion' }
              ]} 
            />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 tracking-tight">Start a Discussion</h1>
          <p className="text-gray-500 font-medium text-sm">Post a persistent topic for your community.</p>
        </div>
      </div>

      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-8"
      >
        <div className="bg-white rounded-3xl border border-gray-100 p-8 space-y-6 shadow-xl shadow-gray-100/50">
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="block text-xs font-bold text-gray-400 uppercase tracking-[0.15em]">Topic Title</label>
              <span className={cn("text-xxs font-bold", title.length > TITLE_LIMIT ? "text-red-500" : "text-gray-400")}>
                {title.length}/{TITLE_LIMIT}
              </span>
            </div>
            <input 
              type="text" 
              value={title}
              onChange={(e) => setTitle(e.target.value.slice(0, TITLE_LIMIT + 10))}
              placeholder="What do you want to discuss?"
              className={cn(
                "w-full px-6 py-4 bg-gray-50 border-none rounded-2xl focus:ring-2 focus:ring-brand-indigo/20 font-bold transition-all text-lg",
                title.length > TITLE_LIMIT && "ring-2 ring-red-500/20 text-red-600"
              )}
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-gray-400 uppercase tracking-[0.15em] mb-3">Category / Tag</label>
            <div className="flex flex-wrap gap-2">
               {['General', 'Tech', 'Governance', 'Proposal', 'Help'].map(tag => (
                 <button key={tag} className="px-4 py-2 bg-gray-50 text-gray-500 rounded-xl font-bold text-xs hover:bg-brand-indigo/10 hover:text-brand-indigo transition-all">
                    #{tag}
                 </button>
               ))}
               <button className="px-4 py-2 border-2 border-dashed border-gray-100 text-gray-300 rounded-xl font-bold text-xs">
                  + Add Tag
               </button>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="block text-xs font-bold text-gray-400 uppercase tracking-[0.15em]">Content</label>
              <span className={cn("text-xxs font-bold", content.length > CONTENT_LIMIT ? "text-red-500" : "text-gray-400")}>
                {content.length}/{CONTENT_LIMIT}
              </span>
            </div>
            <textarea 
              rows="8"
              value={content}
              onChange={(e) => setContent(e.target.value.slice(0, CONTENT_LIMIT + 100))}
              placeholder="Share your thoughts, questions, or proposals..."
              className={cn(
                "w-full px-6 py-4 bg-gray-50 border-none rounded-2xl focus:ring-2 focus:ring-brand-indigo/20 font-medium transition-all resize-none mb-4",
                content.length > CONTENT_LIMIT && "ring-2 ring-red-500/20"
              )}
            ></textarea>

            {discussionImage && (
              <div className="relative w-full max-h-64 rounded-2xl overflow-hidden mb-4 border border-gray-100 group">
                <img src={discussionImage} alt="Discussion" className="w-full h-full object-cover" />
                <button 
                  onClick={() => setDiscussionImage(null)}
                  className="absolute top-2 right-2 p-1.5 bg-black/50 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <X size={16} />
                </button>
              </div>
            )}
          </div>

          <div className="flex gap-2">
             <input 
              type="file" 
              ref={fileInputRef} 
              onChange={handleFileChange} 
              accept="image/*" 
              className="hidden" 
             />
             <button 
              onClick={() => fileInputRef.current?.click()}
              className="flex items-center gap-2 px-4 py-2 bg-brand-indigo/10 text-brand-indigo rounded-xl font-bold text-xs hover:bg-brand-indigo/20 transition-all"
             >
                <ImageIcon size={16} />
                {discussionImage ? 'Change Image' : 'Attach Image'}
             </button>
             <button className="flex items-center gap-2 px-4 py-2 bg-gray-50 text-gray-500 rounded-xl font-bold text-xs hover:bg-gray-100 transition-all">
                <FileText size={16} />
                Add Document
             </button>
          </div>
        </div>

        <div className="bg-white rounded-3xl border border-gray-100 p-8 space-y-6 shadow-xl shadow-gray-100/50">
          <h2 className="text-xs font-bold text-gray-400 uppercase tracking-[0.15em]">Visibility & Access</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <button 
              onClick={() => setPrivacy('public')}
              className={cn(
                "flex items-center gap-4 p-4 rounded-2xl border-2 transition-all text-left",
                privacy === 'public' ? "border-brand-indigo bg-brand-indigo/5" : "border-gray-50 bg-white"
              )}
            >
              <Globe size={20} className={privacy === 'public' ? "text-brand-indigo" : "text-gray-400"} />
              <div>
                <p className="font-bold text-gray-900 text-sm">Public Discussion</p>
                <p className="text-xs text-gray-500 font-medium">Visible to everyone in the community</p>
              </div>
            </button>
            <button 
              onClick={() => setPrivacy('private')}
              className={cn(
                "flex items-center gap-4 p-4 rounded-2xl border-2 transition-all text-left",
                privacy === 'private' ? "border-brand-indigo bg-brand-indigo/5" : "border-gray-50 bg-white"
              )}
            >
              <Lock size={20} className={privacy === 'private' ? "text-brand-indigo" : "text-gray-400"} />
              <div>
                <p className="font-bold text-gray-900 text-sm">Private / Password Protected</p>
                <p className="text-xs text-gray-500 font-medium">Only accessible via link and password</p>
              </div>
            </button>
          </div>

          {privacy === 'private' && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="space-y-4 pt-2"
            >
              <div className="bg-gray-50 rounded-2xl p-5 space-y-4">
                <button 
                  onClick={() => setInviteOnly(!inviteOnly)}
                  className="w-full flex items-center justify-between group"
                >
                  <div className="flex items-center gap-3">
                    <div className={cn("p-2 rounded-lg transition-colors", inviteOnly ? "bg-brand-indigo/10 text-brand-indigo" : "bg-white text-gray-400 shadow-sm")}>
                      <Shield size={16} />
                    </div>
                    <div className="text-left">
                      <p className="font-bold text-sm text-gray-900">Invite Only</p>
                      <p className="text-xs text-gray-500 font-medium">Restrict to approved members</p>
                    </div>
                  </div>
                  <div className={cn("w-10 h-6 rounded-full relative transition-colors", inviteOnly ? "bg-brand-indigo" : "bg-gray-300")}>
                    <div className={cn("absolute top-1 w-4 h-4 bg-white rounded-full transition-all", inviteOnly ? "right-1" : "left-1")}></div>
                  </div>
                </button>

                <div className="h-px bg-gray-200 w-full"></div>

                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-gray-400">
                    <Key size={14} />
                    <label className="text-xs font-bold uppercase tracking-[0.15em]">Access Password</label>
                  </div>
                  <input 
                    type="text"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Set a password for this topic"
                    className="w-full px-4 py-2.5 bg-white border-none rounded-xl focus:ring-2 focus:ring-brand-indigo/10 font-bold text-sm transition-all"
                  />
                </div>
              </div>
            </motion.div>
          )}
        </div>

        <button 
          onClick={handleCreate}
          disabled={isSubmitting || title.length > TITLE_LIMIT || content.length > CONTENT_LIMIT}
          className="w-full bg-brand-indigo text-white font-bold py-5 rounded-2xl shadow-xl shadow-brand-indigo/20 flex items-center justify-center gap-2 hover:bg-brand-indigo/90 transition-all active:scale-[0.98] text-lg disabled:opacity-50 disabled:cursor-not-allowed active:scale-100"
        >
          {isSubmitting ? <Loader2 className="animate-spin" /> : 'Post Discussion'}
          {!isSubmitting && <ArrowRight size={20} />}
        </button>
      </motion.div>
    </div>
  );
};

export default DiscussionCreation;
